import useHoverDelay, { checkTallOrWideImage } from "helpers/utility/utilities";
import { getIsCardslistLoading } from "redux/SingleDataset/selectors";
import ImageWithFallback from "views/components/ImageWithFallback";
import ScrollAwareHover from "views/components/ScrollAwareHover";
import { useEffect, useState, useMemo } from "react";
import { CardBodyImage, OnUpdateAssetData, RelevanceType } from "types";
import { connect } from "react-redux";
import classNames from "classnames";

import styles from "./style.module.scss";
import RelevanceTypeIcon from "views/uikit/RelevanceTypeIcon";
import { FrameIndication } from "../FrameIndication/FrameIndication";
import { isNullOrUndefined } from "../../../../helpers/utility/isNullOrUndefined";

const StripeImage = ({
  plainImage,
  thumbImage,
  videoURI,
  hoverDelay,
  relevanceType,
  showAssetPreview,
  isCardslistLoading,
  onUpdateAssetData,
  onEndContentLoading,
}: StripeImageProps) => {
  const [loading, setLoading] = useState(true);

  const showRelevance = relevanceType && !loading;

  const tableColumnClassNames = classNames(styles.tableColumn, {
    [styles.tableColumnForAssetPreview]: showAssetPreview,
  });

  const updateAssetDataWithValues = () => {
    const source = plainImage.src;
    onUpdateAssetData(plainImage.id, source);
  };

  const { handleMouseEnter, handleMouseLeave } = useHoverDelay(
    updateAssetDataWithValues,
    hoverDelay,
  );

  const handleImageLoad = () => {
    setLoading(false);
    if (isCardslistLoading) {
      onEndContentLoading();
    }
  };

  return (
    <ScrollAwareHover
      className={tableColumnClassNames}
      onMouseOver={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <>
        {!isNullOrUndefined(plainImage.timestamp) && (
          <FrameIndication timestamp={plainImage.timestamp!} />
        )}
        {showRelevance && <RelevanceTypeIcon type={relevanceType} />}
        <ImageWithFallback
          thumbImage={thumbImage}
          plainImage={plainImage}
          onLoad={handleImageLoad}
          className={styles.singleSquareImage}
        />
      </>
    </ScrollAwareHover>
  );
};

const StripeImageBox = ({
  showAssetPreview,
  plainImages,
  thumbImages,
  hoverDelay,
  videoURIs,
  onUpdateAssetData,
  onEndContentLoading,
  isCardslistLoading,
}: StripeImageBoxProps) => {
  const images = useMemo(() => {
    return thumbImages.length > 0 ? thumbImages : plainImages;
  }, [thumbImages, plainImages]);
  const [imageCount, setImageCount] = useState(0);
  const [gridLayout, setGridLayout] = useState<null | "tall" | "wide">(null);

  const gridStyle = () => {
    if (gridLayout) {
      const isTall = gridLayout === "tall";
      return {
        gridTemplateColumns: `repeat(${
          isTall ? imageCount : 1
        }, minmax(1px, 1fr))`,
        gridTemplateRows: `repeat(${
          isTall ? 1 : imageCount
        }, minmax(1px, 1fr))`,
      };
    }
    return undefined;
  };

  const mergedStyle: any = {
    ...gridStyle(),
    visibility: !!gridLayout ? "visible" : "hidden",
  };

  useEffect(() => {
    if (images.length > 0) {
      if (images.length === 3) setImageCount(3);
      else setImageCount(2);

      let wideCount = 0;
      let tallCount = 0;

      Promise.all(
        images.map(async (image) => {
          try {
            const type = await checkTallOrWideImage(image.src);
            if (type === "tall") tallCount += 1;
            else if (type === "wide") wideCount += 1;
          } catch (err) {
            console.error(`Error checking image: ${image?.src}`, err);
          }
        }),
      ).then(() => {
        if (tallCount > wideCount) setGridLayout("tall");
        else setGridLayout("wide");
      });
    }
  }, [images]);

  return (
    <div
      style={mergedStyle}
      data-testid="stripe-images-container"
      className={classNames(styles.stripeImagesContainer, {})}
    >
      {plainImages.length > 0 &&
        plainImages.map((plainImage, i: number) => {
          const thumbImage = thumbImages && thumbImages[i];
          const relevanceType = plainImage.relevanceType;
          return (
            i < imageCount * imageCount && (
              <StripeImage
                key={i}
                plainImage={plainImage}
                thumbImage={thumbImage}
                videoURI={videoURIs[i]}
                hoverDelay={hoverDelay}
                relevanceType={relevanceType}
                showAssetPreview={showAssetPreview}
                isCardslistLoading={isCardslistLoading}
                onUpdateAssetData={onUpdateAssetData}
                onEndContentLoading={onEndContentLoading}
              />
            )
          );
        })}
    </div>
  );
};

const mapStateToProps = (state: any) => {
  return { isCardslistLoading: getIsCardslistLoading(state) };
};

export default connect(mapStateToProps)(StripeImageBox);

interface BaseStripeImageProps {
  showAssetPreview: boolean;
  hoverDelay: number;
  onUpdateAssetData: OnUpdateAssetData;
}

export interface StripeImageBoxProps extends BaseStripeImageProps {
  plainImages: CardBodyImage[];
  thumbImages: CardBodyImage[];
  videoURIs: string[];
  isCardslistLoading: boolean;
  onEndContentLoading: () => void;
}

interface StripeImageProps extends BaseStripeImageProps {
  plainImage: CardBodyImage;
  thumbImage: CardBodyImage;
  videoURI: string;
  isCardslistLoading: boolean;
  relevanceType: RelevanceType | null;
  onEndContentLoading: () => void;
}
